import React from 'react';
import goldColors from '../themes/goldColors';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Avatar,
  alpha,
  LinearProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useLanguage } from '../contexts/LanguageContext';

import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SavingsIcon from '@mui/icons-material/Savings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

const SidebarWrapper = styled(Box)(({ theme }) => ({
  width: 220,
  flexShrink: 0,
  overflowY: 'auto',
  backgroundColor: theme.palette.background.sidebar,
  height: '100vh',
  borderRight: `1px solid ${theme.palette.divider}`,
}));

const UserSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 1.5),
  display: 'flex',
  alignItems: 'center',
}));

const StepButton = styled(ListItemButton)(({ theme }) => ({
  borderRadius: '4px',
  padding: theme.spacing(0.6, 1.5),
  '&.Mui-selected': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
}));

// Barra lateral reducida para clientes que todavía no terminan el
// onboarding. A propósito NO muestra Tablero ni Calendario — esas
// secciones solo tienen sentido una vez que hay datos reales cargados
// (cuentas, deudas, perfil). En vez de eso, muestra los pasos pendientes
// como checklist, para que el cliente sepa exactamente qué le falta.
const OnboardingSidebar = ({ onboarding }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language, t } = useLanguage();
  const { user } = useSelector((state) => state.auth);

  const getUserInitials = () => {
    if (!user) return 'U';
    const displayName = user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim();
    if (!displayName) {
      return user.email ? user.email.charAt(0).toUpperCase() : 'U';
    }
    return displayName
      .split(' ')
      .filter(Boolean)
      .map((part) => part.charAt(0))
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  const steps = [
    {
      key: 'profile',
      path: '/profile',
      done: !!onboarding?.profile_completed,
      icon: PersonIcon,
      label: language === 'es' ? 'Perfil' : 'Profile',
    },
    {
      key: 'accounts',
      path: '/accounts',
      done: !!onboarding?.accounts_completed,
      icon: AccountBalanceWalletIcon,
      label: language === 'es' ? 'Cuentas bancarias' : 'Bank accounts',
    },
    {
      key: 'debts',
      path: '/debts',
      done: !!onboarding?.debts_completed,
      icon: SavingsIcon,
      label: language === 'es' ? 'Deudas' : 'Debts',
    },
  ];

  const completedCount = steps.filter((s) => s.done).length;

  return (
    <SidebarWrapper>
      <UserSection>
        <Avatar
          sx={{ width: 32, height: 32, fontSize: '0.875rem', bgcolor: 'primary.main', mr: 1.5 }}
        >
          {getUserInitials()}
        </Avatar>
        <Typography variant="body2" fontWeight={500} noWrap>
          {user?.displayName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || t('common.guest', 'Guest')}
        </Typography>
      </UserSection>

      <Divider />

      <Box sx={{ p: 2 }}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
          {language === 'es' ? '¡Bienvenido/a!' : 'Welcome!'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {language === 'es'
            ? 'Completa estos pasos para activar tu tablero.'
            : 'Finish these steps to unlock your dashboard.'}
        </Typography>
        <Box sx={{ mt: 1.5, mb: 0.5 }}>
          <LinearProgress
            variant="determinate"
            value={(completedCount / steps.length) * 100}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary">
          {completedCount}/{steps.length} {language === 'es' ? 'completados' : 'done'}
        </Typography>
      </Box>

      <Divider />

      <List disablePadding sx={{ py: 1 }}>
        {steps.map((step) => {
          const Icon = step.icon;
          const selected = location.pathname === step.path;
          return (
            <ListItem key={step.key} disablePadding sx={{ px: 1 }}>
              <StepButton selected={selected} onClick={() => navigate(step.path)}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {step.done ? (
                    <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
                  ) : (
                    <RadioButtonUncheckedIcon fontSize="small" sx={{ color: goldColors.main }} />
                  )}
                </ListItemIcon>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <Icon fontSize="small" sx={{ color: goldColors.main }} />
                </ListItemIcon>
                <ListItemText
                  primary={step.label}
                  primaryTypographyProps={{
                    fontSize: '0.8rem',
                    sx: step.done ? { textDecoration: 'line-through', color: 'text.secondary' } : {},
                  }}
                />
              </StepButton>
            </ListItem>
          );
        })}
      </List>
    </SidebarWrapper>
  );
};

export default OnboardingSidebar;
